// Anonymous read of a Speed Pass upgrade offer from Identity, for the banner on the public lap
// page. The offer id in the email link is the only key. Never sends the auth token, and any
// failure (unknown id 404, rate limit 429, network, CORS) is just "no offer": the banner is an
// extra, so it disappears rather than showing an error.

import { SpeedPassOffer } from '../models/speedPass/SpeedPassOffer';

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isOfferId = (value: string | null | undefined): value is string => !!value && GUID.test(value);

const getIdentityBaseUrl = (): string => {
  const explicit = process.env.REACT_APP_IDENTITY_API_BASE_URL;
  if (!explicit) {
    throw new Error('Missing REACT_APP_IDENTITY_API_BASE_URL environment variable');
  }
  return explicit.endsWith('/') ? explicit : explicit + '/';
};

/** GET {identity}/public/speed-pass/offers/{id}, or null for anything but a 200 with a body. */
export async function getSpeedPassOffer(offerId: string, signal?: AbortSignal): Promise<SpeedPassOffer | null> {
  // Identity's route is {id:guid}: anything else is a guaranteed 404, so don't spend a request on it.
  if (!isOfferId(offerId)) return null;
  try {
    const response = await fetch(`${getIdentityBaseUrl()}public/speed-pass/offers/${offerId}`, {
      method: 'GET',
      credentials: 'omit',
      signal,
    });
    if (!response.ok) return null;
    const offer = (await response.json()) as SpeedPassOffer | null;
    return offer && typeof offer === 'object' ? offer : null;
  } catch {
    return null;
  }
}
